/**
 * Modal dialog.
 *
 * Built on the native `<dialog>` element, which gives focus trapping, the top layer, `Escape`, and
 * inert background content from the platform. The hand-rolled equivalent is several hundred lines
 * that are wrong in at least one browser, and the failure mode — focus escaping to the page behind
 * — is invisible to anyone testing with a mouse.
 *
 * Closing is always possible. A modal an administrator cannot dismiss is one they will force-quit
 * the tab to escape, losing whatever they were doing.
 */

import { useEffect, useRef, type ReactNode } from "react";

import { cx } from "./cx";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Actions in the footer, inline-end aligned so they mirror. */
  footer?: ReactNode;
  className?: string;
  /** Accessible label for the close control, localized by the caller. */
  closeLabel: string;
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  className,
  closeLabel,
}: ModalProps): JSX.Element {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      // `Escape` fires `cancel`; without this the element closes but React still thinks it is open.
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClose={onClose}
      className={cx(
        "w-full max-w-prose rounded-md border border-borderSubtle bg-surfaceRaised p-0",
        "text-textPrimary shadow-overlay backdrop:bg-surfaceInverse/40",
        className,
      )}
      aria-labelledby="modal-title"
    >
      <header className="flex items-center justify-between gap-4 border-b border-borderSubtle px-4 py-3">
        <h2 id="modal-title" className="text-heading-sm">
          {title}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={closeLabel}
          className="rounded-sm px-2 py-1 text-body-md text-textMuted hover:bg-surfaceSunken focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
        >
          <span aria-hidden="true">×</span>
        </button>
      </header>

      <div className="px-4 py-4">{children}</div>

      {footer ? (
        <footer className="flex flex-wrap justify-end gap-2 border-t border-borderSubtle px-4 py-3">
          {footer}
        </footer>
      ) : null}
    </dialog>
  );
}
